import { IQuery } from 'core-ts-types';
import { ScheduledSectionStatus } from '../../../views/course_scheduler/types/scheduled_section_status_enum';
import { ISectionsDatasource } from '../../../views/sections/datasources/sections_datasource';
import { TermProgramInfoModel } from '../../../views/term_courses/models/term_program_info_model';
import { ORMMediator } from 'utility-ts-orm';

import * as _4 from 'lodash4';
import { Section } from '../../../views/sections/builders/section_builder';
import { SectionInfoServant } from '../../../views/sections/servants/section_info_servant';
import { ScheduledSectionStatusServant } from '../../../views/course_scheduler/servants/scheduled_section_status_servant';
import { SelectedUnscheduledSectionModel } from '../../../views/unscheduled_sections/models/selected_unscheduled_section_model';
import { TimelineServant } from '../servants/timeline_servant';
import {
  ITimelineHeader,
  ITimeBlocks,
  IGroupsBlock,
  ITimelineFooter,
  ISection,
  ITermSession,
  ITimelineTimeblock
} from '../../../interfaces/scheduler_interfaces';
import { SectionTypeFilters } from '../../../views/term_courses/filters/section_type_filters';
import { UnscheduledSectionServant } from '../../../views/unscheduled_sections/servants/unscheduled_section_servant';
import { ITermSessionsDatasource } from '../../../views/term_courses/datasources/term_sessions_datasource';

import { TimelineThemeStrategy } from '../strategies/timeline_theme_strategy';
import { CalendarTimesModel } from '../../../views/calendar/models/calendar_times_model';
import { IWaitlistStatusDatasource } from '../../../views/course_scheduler/datasources/waitlist_status_datasource';
import { ISectionAvailabilityDatasource } from '../../../views/sections/datasources/section_availability_datasource';
export class TimelineQuery implements IQuery {
  get result() {
    return this._result;
  }

  private _result: {
    headers: ITimelineHeader[];
    timeblocks: ITimeBlocks[];
    groups: IGroupsBlock[];
    footer: ITimelineFooter[];
  };
  constructor(
    private ngSectionsDatasource: ISectionsDatasource,
    private ngTermSessionsDatasource: ITermSessionsDatasource,
    private ngTermProgramInfoModel: TermProgramInfoModel,
    private ngSectionInfoServant: SectionInfoServant,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant,
    private ngTimelineServant: TimelineServant,
    private ngSectionTypeFilters: SectionTypeFilters,
    private ngUnscheduledSectionServant: UnscheduledSectionServant,
    private getCSSClassForString: any,
    private ngCalendarTimesModel: CalendarTimesModel,
    private ngSelectedUnscheduledSectionModel: SelectedUnscheduledSectionModel,
    private ngWaitlistStatusDatasource: IWaitlistStatusDatasource,
    private ngSectionAvailabilityDatasource: ISectionAvailabilityDatasource
  ) {
    ORMMediator.registerQuery(
      this,
      ngWaitlistStatusDatasource,
      ngSectionAvailabilityDatasource,
      ngCalendarTimesModel,
      ngSectionsDatasource,
      ngSelectedUnscheduledSectionModel
    );
  }
  run() {
    const termInfo = this.ngTermProgramInfoModel.read();
    const sortedSections = _4.sortBy(
      this.ngSectionsDatasource.query((section) =>
        _4.includes(
          [
            ...ScheduledSectionStatus.getRegisteredSectionStatuses(),
            ...ScheduledSectionStatus.getScheduledSectionStatuses(),
            ...ScheduledSectionStatus.getWaitlistedSectionStatuses()
          ],
          section.status
        )
      ),
      ['course', 'class_start_dt']
    );

    const sectionsWithCourses = this.ngSectionsDatasource.metasource().composes(sortedSections);
    const previewSection = this.ngSelectedUnscheduledSectionModel.read();

    if (!_4.isEmpty(previewSection._course)) {
      previewSection.status = 'PREVIEW';
      sectionsWithCourses.push(previewSection);
    }

    // SGD-20093 - Scheduler designed to show sections spans within a particular term and with this ticket
    // we're enforcing the idea of term start and end date will super cede the section start and end date
    let timelineStartDate = moment(_4.get(termInfo, 'term.term_start_dt'));

    const timelineEndDate = moment(_4.get(termInfo, 'term.term_end_dt'));

    // TIMELINE HEADERS
    let headers: ITimelineHeader[] | any[] = [];
    let footer: any = [];
    const headerRange: any = [];

    while (timelineEndDate.isSameOrAfter(timelineStartDate)) {
      headerRange.push(timelineStartDate.format('YYYY-MM-DD'));
      timelineStartDate = timelineStartDate.add(1, 'month');
      if (
        timelineStartDate.month() === timelineEndDate.month() &&
        timelineStartDate.year() === timelineEndDate.year()
      ) {
        // Reached end of range
        headerRange.push(timelineEndDate.format('YYYY-MM-DD'));
        break;
      }
    }

    headers = this.ngTimelineServant.getHeaders(headerRange);
    footer = this.ngTimelineServant.getFooters(
      sectionsWithCourses,
      _4.get(termInfo, 'term.term_start_dt'),
      _4.get(termInfo, 'term.term_end_dt')
    );

    // TIMEBLOCKS
    let timeblockResults: ITimeBlocks[] = [];
    const groupedTimeblocks: IGroupsBlock[] = [];

    _4.each(sectionsWithCourses, (section: Section, index: number, collection: Section[]) => {
      const { fromDate, toDate } = TimelineServant.getTimelineDateRange(
        termInfo.term!,
        section.class_start_dt,
        section.class_end_dt
      );
      const dateRangeForBodyText = this.ngSectionInfoServant.convertDateToRange(fromDate, toDate, undefined, 'MMM Do');

      const isRegistered =
        this.ngScheduledSectionStatusServant.isRegisteredOrInprogress(section) ||
        this.ngScheduledSectionStatusServant.isCompleted(section);
      const isShortSection = this.ngSectionTypeFilters.isSectionDurationShorterThanTerm(section);
      const isPreview = section.status === 'PREVIEW';
      const timeblockColor = isPreview ? 'clr-6-bkg' : this.getCSSClassForString(_4.get(section, '_course.nk'), 'bkg');
      const isOnline = this.ngUnscheduledSectionServant.isOnlineSection(section);

      const blocks: ITimelineTimeblock[] = this.ngTimelineServant.getMultipleMeetingTimes(section, termInfo.term!);

      const timeblock = this.ngTimelineServant.getTimelineBlock({
        section,
        isRegistered,
        timeblockColor: timeblockColor ? TimelineThemeStrategy.get(timeblockColor) : {},
        dateRangeForBody: dateRangeForBodyText,
        isShortSection,
        isOnline,
        blocks,
        timeblockResults
      });

      timeblockResults.push(timeblock);
    });
    timeblockResults = this.ngTimelineServant.getUniqueDates(timeblockResults);
    timeblockResults = this.ngTimelineServant.sortBySectionStartDate(timeblockResults);
    timeblockResults = this.ngTimelineServant.prependBlockCount(timeblockResults);
    // END TIMEBLOCKS
    this._result = {
      headers,
      timeblocks: timeblockResults,
      groups: groupedTimeblocks,
      footer
    };
    return this;
  }
}

angular.module('capability-ng-scheduler').service('ngTimelineQuery', TimelineQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/components/timeline/queries/timeline_query.ts